import React, { useState } from 'react';

const VARIABLE_GROUPS = [
  {
    label: 'Dates',
    vars: [
      { name: '%STARTDATE%', desc: 'Engagement start date' },
      { name: '%ENDDATE%', desc: 'Engagement end date' },
      { name: '%REPORTDATE%', desc: 'Report generation date' },
      { name: '%REPORTVERSION%', desc: 'Version of this report (e.g. 2, or 1.1 for a revision)' },
      { name: '%INREVIEWDATE%', desc: 'Date the engagement was put into In Review' },
      { name: '%COMPLETEDATE%', desc: 'Date the engagement was put into Complete' },
      { name: '%RETESTSTARTDATE%', desc: 'Date the engagement was put into In Retest' },
      { name: '%RETESTENDDATE%', desc: 'Date the engagement was put into Retest Complete' },
    ]
  },
  {
    label: 'OWASP',
    vars: [
      { name: '%OWASPCATEGORIES%', desc: 'How many OWASP Top 10 categories have findings' },
      { name: '%OWASPCATEGORIESLIST%', desc: 'Those categories, e.g. A01:2021 Broken Access Control' },
      { name: '%OWASPUNCLASSIFIED%', desc: 'Findings with no OWASP category set' },
    ]
  },
  {
    label: 'Finding status',
    vars: [
      { name: '%EXPLOITABLECOUNT%', desc: 'Findings marked directly exploitable' },
      { name: '%OPENCOUNT%', desc: 'Findings still Open' },
      { name: '%CLOSEDCOUNT%', desc: 'Findings verified Closed' },
      { name: '%REVALIDATEDCOUNT%', desc: 'Findings revalidated in this retest (0 if there has been none)' },
      { name: '%RETESTCLOSEDCOUNT%', desc: 'Revalidated findings that ended Closed' },
      { name: '%RETESTOPENCOUNT%', desc: 'Revalidated findings that did not' },
    ]
  },
  {
    label: 'Client',
    vars: [
      { name: '%CLIENTNAME%', desc: 'Client / company name' },
      { name: '%CONTACTNAME%', desc: 'Client contact name' },
      { name: '%CONTACTEMAIL%', desc: 'Client contact email' },
      { name: '%VENDORNAME%', desc: 'Delivering vendor — falls back to the client name' },
    ]
  },
  {
    label: 'Engagement',
    vars: [
      { name: '%APPLICATIONNAME%', desc: 'Linked application name' },
      { name: '%ENGAGEMENTNAME%', desc: 'Engagement name' },
      { name: '%ENGAGEMENTTYPE%', desc: 'Type (e.g. Network Pentest)' },
      { name: '%ENGAGEMENTSTATUS%', desc: 'Current status' },
      { name: '%ENGAGEMENTSEVERITY%', desc: 'Highest severity found on this engagement, or None' },
      { name: '%TESTERNAME%', desc: 'Assigned tester name' },
      { name: '%TRACKINGID%', desc: 'Customer tracking ID' },
      { name: '%SERVICEORDER%', desc: 'Service order number' },
      { name: '%ENVIRONMENTTYPE%', desc: 'Environment type (Production, DEV, etc.)' },
      { name: '%TESTINGAPPROACH%', desc: 'Black Box - Unauthenticated, or Gray Box - Authenticated' },
      { name: '%TECHNICALMANAGER%', desc: 'Technical manager (auto-set on Complete)' },
      { name: '%TESTEREMAIL%', desc: 'Assigned tester email' },
      { name: '%TECHNICALMANAGEREMAIL%', desc: 'Technical manager email' },
    ]
  },
  {
    label: 'Findings',
    vars: [
      { name: '%TOTALFINDINGS%', desc: 'Total finding count' },
      { name: '%CRITICALCOUNT%', desc: 'Critical findings' },
      { name: '%HIGHCOUNT%', desc: 'High findings' },
      { name: '%MEDIUMCOUNT%', desc: 'Medium findings' },
      { name: '%LOWCOUNT%', desc: 'Low findings' },
      { name: '%INFOCOUNT%', desc: 'Informational findings' },
    ]
  },
  {
    label: 'Scope',
    vars: [
      { name: '%SCOPECOUNT%', desc: 'Number of scope items' },
      { name: '%SCOPEINFO%', desc: 'All scope items (type, value, description)' },
    ]
  },
];

export default function VariableReference() {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(null);

  const copyVar = (varName) => {
    navigator.clipboard.writeText(varName).then(() => {
      setCopied(varName);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  return (
    <div style={{ marginTop: 16 }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: 0, background: 'none', border: 'none',
          cursor: 'pointer', color: 'var(--text-secondary)'
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Template Variables
        </span>
        <span style={{
          fontSize: 12, transform: expanded ? 'rotate(0)' : 'rotate(-90deg)',
          transition: 'transform 0.2s'
        }}>▼</span>
      </button>

      {expanded && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.4 }}>
            Use these in any text section. Click to copy. Variables are replaced with real data when generating the PDF.
          </div>
          {VARIABLE_GROUPS.map(group => (
            <div key={group.label} style={{ marginBottom: 8 }}>
              <div style={{
                fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)',
                textTransform: 'uppercase', letterSpacing: '0.05em',
                padding: '4px 0', borderBottom: '1px solid var(--border)', marginBottom: 4
              }}>
                {group.label}
              </div>
              {group.vars.map(v => (
                <button
                  key={v.name}
                  onClick={() => copyVar(v.name)}
                  title={`Click to copy ${v.name}`}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '3px 6px', background: 'none', border: 'none',
                    cursor: 'pointer', color: 'var(--text)', borderRadius: 3,
                    textAlign: 'left', transition: 'background 0.15s', fontSize: 11
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'var(--bg-input)'}
                  onMouseOut={e => e.currentTarget.style.background = 'none'}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <code style={{
                      fontSize: 10, fontFamily: 'monospace', color: 'var(--primary)',
                      fontWeight: 600
                    }}>{v.name}</code>
                    <span style={{ fontSize: 9, color: 'var(--text-secondary)' }}>{v.desc}</span>
                  </div>
                  <span style={{ fontSize: 9, color: copied === v.name ? '#22c55e' : 'var(--text-secondary)', flexShrink: 0, marginLeft: 4 }}>
                    {copied === v.name ? '✓' : '⎘'}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
